import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Package as PackageIcon, Users, CreditCard, Calendar, Mail, Phone, MapPin, Coins, Edit, X } from 'lucide-react';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { BASE_URL } from '../config';

export default function HospitalUserDashboard() {
  const [hospitalInfo, setHospitalInfo] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    contact_number: '',
    email: '',
    established_year: ''
  });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchHospitalInfo();
    fetchDoctors();
  }, []);

  const fetchHospitalInfo = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const hospitalId = localStorage.getItem('hospitalId');
      
      // console.log('🔍 Fetching hospital info for ID:', hospitalId);
      
      // Fetch complete details from superadmin table
      const response = await axios.get(`${BASE_URL}/api/superadmin/${hospitalId}/complete-details/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('📦 Hospital info received:', response.data);
      console.log('📞 Contact number:', response.data.contact_number);
      console.log('📍 Address:', response.data.address);
      
      setHospitalInfo(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching hospital info:', error);
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const hospitalId = localStorage.getItem('hospitalId');
      
      const response = await axios.get(`${BASE_URL}/api/superadmin/${hospitalId}/doctors/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDoctors(response.data.doctors || []);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const handleOpenUpdateModal = () => {
    setFormData({
      name: hospitalInfo?.name || '',
      address: hospitalInfo?.address || '',
      contact_number: hospitalInfo?.contact_number || '',
      email: hospitalInfo?.email || '',
      established_year: hospitalInfo?.established_year || ''
    });
    setShowUpdateModal(true);
  };

  const handleUpdateDetails = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const token = localStorage.getItem('accessToken');
      
      await axios.put(
        `${BASE_URL}/api/hospitals/details/`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Contact details updated successfully!');
      setShowUpdateModal(false);
      fetchHospitalInfo(); // Refresh data
    } catch (error) {
      console.error('Error updating details:', error);
      toast.error(error.response?.data?.error || 'Failed to update details');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Hospital Dashboard</h1>
          <p className="text-sm text-slate-600 mt-1">Overview of your hospital information and doctors</p>
        </div>
        <button
          onClick={handleOpenUpdateModal}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors text-sm"
        >
          <Edit className="w-4 h-4" />
          Update Contact Details
        </button>
      </div>

      {/* Hospital Information Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg border border-slate-200 shadow-xl overflow-hidden"
      >
        <div className="bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-slate-900" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{hospitalInfo?.name || hospitalInfo?.username}</h2>
              <p className="text-sm text-primary-100">Hospital Information</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                Contact Details
              </h3>
              
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-600 mb-1">Email Address</p>
                  <p className="text-sm text-slate-900 font-medium">{hospitalInfo?.email || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-600 mb-1">Contact Number</p>
                  <p className="text-sm text-slate-900 font-medium">{hospitalInfo?.contact_number || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-600 mb-1">Address</p>
                  <p className="text-sm text-slate-900 font-medium">{hospitalInfo?.address || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Package & Credits Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                Package & Credits
              </h3>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="w-10 h-10 bg-primary-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <PackageIcon className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-600 mb-1">Current Package</p>
                  <p className="text-sm text-slate-900 font-semibold">{hospitalInfo?.package_name || 'No Package'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-600 mb-1">Package Credits</p>
                  <p className="text-sm text-slate-900 font-semibold">{hospitalInfo?.credit_limit || '0'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Coins className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-600 mb-1">Add-On Credits</p>
                  <p className="text-sm text-slate-900 font-semibold">{hospitalInfo?.add_on || '0'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-600 mb-1">Days Limit</p>
                  <p className="text-sm text-slate-900 font-semibold">{hospitalInfo?.days_limit || '0'} days</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Doctors Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg border border-slate-200 shadow-xl overflow-hidden"
      >
        <div className="bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-slate-900" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Doctors</h2>
                <p className="text-sm text-primary-100">{doctors.length} doctor{doctors.length !== 1 ? 's' : ''} registered</p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          {doctors.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Email</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Package</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {doctors.map((doctor, index) => (
                    <motion.tr
                      key={doctor.user_ID}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-b border-slate-200/50 hover:bg-slate-50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary-500/20 rounded-full flex items-center justify-center">
                            <span className="text-primary-400 font-semibold text-sm">
                              {doctor.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="text-slate-900 font-medium text-sm">{doctor.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-sm">{doctor.email}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500/20 text-primary-300 border border-primary-500/30">
                          {doctor.package || 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-slate-900 font-semibold text-sm">
                          {doctor.credits_remaining !== null ? doctor.credits_remaining.toFixed(2) : 'Unlimited'}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-600 text-lg">No doctors registered yet</p>
              <p className="text-slate-500 text-sm mt-2">Contact your administrator to add doctors</p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Update Contact Details Modal */}
      <AnimatePresence>
        {showUpdateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowUpdateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-lg border border-slate-200 shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="bg-gradient-to-r from-primary-600 to-primary-500 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Edit className="w-5 h-5 text-slate-900" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">Update Contact Details</h3>
                </div>
                <button
                  onClick={() => setShowUpdateModal(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-900" />
                </button>
              </div>

              <form onSubmit={handleUpdateDetails} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Hospital Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Enter hospital name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Enter email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Contact Number
                  </label>
                  <input
                    type="tel"
                    value={formData.contact_number}
                    onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Enter contact number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Address
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    rows={3}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
                    placeholder="Enter hospital address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Established Year
                  </label>
                  <input
                    type="number"
                    value={formData.established_year}
                    onChange={(e) => setFormData({ ...formData, established_year: e.target.value })}
                    min="1800"
                    max={new Date().getFullYear()}
                    className="w-full px-4 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                    placeholder="Enter established year"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowUpdateModal(false)}
                    className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-900 font-medium rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updating}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-primary-500/30"
                  >
                    {updating ? 'Updating...' : 'Update Details'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
